package com.evoapps.evoshift.domain.schedule

import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.SystemContractType
import kotlinx.datetime.LocalDate

/**
 * SH-112: what the labour code needs to know about one person. Unknown (an operational manager
 * may not read pay terms): only the rest rules are checked, as for an employee (SH-133).
 */
data class LabourProfile(
    val contract: SystemContractType = SystemContractType.NOT_SET,
    /** Their daily norm (8 hours, or less in their contract). */
    val dailyNormHours: Double = LabourCode.DAILY_NORM_HOURS,
    /**
     * Their norm for the month (FTE × the month's working hours, or declared hours); null: the
     * full-time norm of the month.
     */
    val monthlyNormHours: Double? = null
) {
    val hoursRulesApply: Boolean get() = LabourCode.hoursRulesApply(contract)

    /** SH-133: the rest rules, by the contract and the workplace's "rest for every contract" setting. */
    fun restRulesApply(restForAllContracts: Boolean): Boolean = LabourCode.restRulesApply(contract, restForAllContracts)

    /** SH-133: the labour code for an employee, the company's policy for anyone else. */
    val basis: LegalBasis get() = LabourCode.basis(contract)
}

/** SH-112: a labour-code rule a roster built by hand breaks. Facts only: the UI words them. */
sealed interface LabourWarning {
    val userId: String
    /** The shifts it is about (marked on the grid); empty for the month as a whole. */
    val shiftIds: List<String>
    /** SH-133: a legal requirement (employment contract) or the workplace's own policy. */
    val basis: LegalBasis

    /**
     * Art. 132: less than 11 hours of rest in the 24 hours from the start of the shift on [date].
     * SH-133: [previousEnd] and [nextStart] (ISO date-times) are the tightest break between two of
     * the shifts, when a break is what is short (null: one shift longer than 13 hours).
     */
    data class ShortDailyRest(
        override val userId: String,
        override val shiftIds: List<String>,
        val date: String,
        val restHours: Double,
        val previousEnd: String? = null,
        val nextStart: String? = null,
        override val basis: LegalBasis = LegalBasis.LABOUR_CODE
    ) : LabourWarning {
        /** Hours between [previousEnd] and [nextStart] (null without them). */
        val breakHours: Double? get() {
            val end = previousEnd?.let { isoMinutes(it) } ?: return null
            val start = nextStart?.let { isoMinutes(it) } ?: return null
            return (start - end) / 60.0
        }
    }

    /** Art. 133: less than 35 hours of uninterrupted rest in the week starting on [weekStart]. */
    data class ShortWeeklyRest(
        override val userId: String,
        override val shiftIds: List<String>,
        val weekStart: String,
        val restHours: Double,
        override val basis: LegalBasis = LegalBasis.LABOUR_CODE
    ) : LabourWarning

    /** Art. 129: [hours] of work on [date], above their daily norm of [normHours]. */
    data class OverDailyNorm(
        override val userId: String,
        override val shiftIds: List<String>,
        val date: String,
        val hours: Double,
        val normHours: Double
    ) : LabourWarning {
        override val basis: LegalBasis get() = LegalBasis.LABOUR_CODE
    }

    /** Art. 131: [averageHours] a week on average this month, above 48. */
    data class OverWeeklyAverage(
        override val userId: String,
        val averageHours: Double
    ) : LabourWarning {
        override val shiftIds: List<String> get() = emptyList()
        override val basis: LegalBasis get() = LegalBasis.LABOUR_CODE
    }

    /** Art. 151: [overtimeHours] of overtime this month, above the month's share of the 150-hour yearly cap. */
    data class OvertimeOverCap(
        override val userId: String,
        val overtimeHours: Double,
        val capHours: Double
    ) : LabourWarning {
        override val shiftIds: List<String> get() = emptyList()
        override val basis: LegalBasis get() = LegalBasis.LABOUR_CODE
    }
}

/** SH-112: the month's warnings, by shift and by person. */
data class LabourReport(val warnings: List<LabourWarning>) {
    val byShiftId: Map<String, List<LabourWarning>> by lazy {
        warnings.flatMap { w -> w.shiftIds.map { it to w } }.groupBy({ it.first }, { it.second })
    }
    val byUserId: Map<String, List<LabourWarning>> by lazy { warnings.groupBy { it.userId } }

    companion object {
        val EMPTY = LabourReport(emptyList())
    }
}

/**
 * SH-112: the labour code's checks on a roster edited by hand (assigning, moving, editing shifts).
 * Soft validation, like conflicts: the manager sees the warnings and may keep the roster as it
 * is. The rules are [LabourCode]'s, the same auto-assign never breaks.
 *
 * - **Daily rest**: at least 11 hours of rest in the 24 hours from each shift's start.
 * - **Weekly rest**: at least 35 uninterrupted hours in each week of the month, weeks
 *   counted from its 1st (the settlement period's first day, art. 128 §3); the last one runs into
 *   the next month.
 * - **Daily norm** (employment contracts): a day's planned hours above the person's norm.
 * - **48 hours a week on average** (employment contracts): the month is the settlement period.
 * - **Overtime cap** (employment contracts): the month's overtime above 12.5 hours, its share of
 *   the 150-hour yearly limit.
 *
 * The rest rules bind employees by law and everyone else only as the workplace's own policy
 * (SH-133, [LabourCode.restRulesApply]); each warning carries its [LegalBasis].
 *
 * Only the roster's shifts are seen (not other workplaces or months), planned times, with
 * unreadable or unassigned shifts left out.
 */
object LabourCodeCheck {

    fun check(
        shifts: List<Shift>,
        year: Int,
        month: Int,
        profiles: Map<String, LabourProfile> = emptyMap(),
        /** Full-time hours of the month (PayrollRules.workingHours). */
        nominalMonthlyHours: Double = 160.0,
        /** SH-133: the workplace applies the rest rules to every contract (WorkplaceSettings). */
        restForAllContracts: Boolean = true
    ): LabourReport {
        val monthStart = LocalDate(year, month, 1)
        val monthEnd = LocalDate(if (month == 12) year + 1 else year, if (month == 12) 1 else month + 1, 1).toEpochDays() - 1
        val days = (monthEnd - monthStart.toEpochDays() + 1).toInt()
        val prefix = "$year-${month.toString().padStart(2, '0')}"
        val warnings = shifts.filter { it.userId != null }.groupBy { it.userId!! }.entries.sortedBy { it.key }.flatMap { (userId, theirs) ->
            val timed = theirs.mapNotNull { s -> s.span()?.let { s to it } }.sortedBy { it.second.start }
            val profile = profiles[userId] ?: LabourProfile()
            buildList {
                if (profile.restRulesApply(restForAllContracts)) {
                    addAll(dailyRest(userId, timed, profile.basis))
                    addAll(weeklyRest(userId, timed, monthStart.toEpochDays(), monthEnd, profile.basis))
                }
                if (profile.hoursRulesApply) {
                    addAll(hours(userId, timed.filter { it.first.date.startsWith(prefix) }, profile, days, nominalMonthlyHours))
                }
            }
        }
        return LabourReport(warnings)
    }

    private fun dailyRest(userId: String, timed: List<Pair<Shift, MinuteSpan>>, basis: LegalBasis): List<LabourWarning> {
        val spans = timed.map { it.second }
        val reported = ArrayList<Set<String>>()
        return timed.mapNotNull { (shift, span) ->
            val rest = LabourCode.dailyRestMinutes(spans, span)
            if (rest >= LabourCode.DAILY_REST_HOURS * 60L) return@mapNotNull null
            // The shift and those starting in its 24 hours; the same shifts are reported once.
            val window = timed.filter { (_, other) -> other.start >= span.start && other.start < span.start + DAY }
            val ids = window.map { it.first.id }
            if (reported.any { it.containsAll(ids) }) return@mapNotNull null
            reported += ids.toSet()
            // SH-133: the tightest break between them, for "the previous shift ends at …".
            val tightest = window.zipWithNext().filter { (a, b) -> b.second.start - a.second.end < LabourCode.DAILY_REST_HOURS * 60L }
                .minByOrNull { (a, b) -> b.second.start - a.second.end }
            LabourWarning.ShortDailyRest(
                userId, ids, shift.date.take(10), rest / 60.0,
                previousEnd = tightest?.first?.first?.plannedTime?.end,
                nextStart = tightest?.second?.first?.plannedTime?.start,
                basis = basis
            )
        }
    }

    private fun weeklyRest(
        userId: String,
        timed: List<Pair<Shift, MinuteSpan>>,
        monthStart: Long,
        monthEnd: Long,
        basis: LegalBasis
    ): List<LabourWarning> {
        val spans = timed.map { it.second }
        return generateSequence(monthStart) { it + 7 }.takeWhile { it <= monthEnd }.mapNotNull { week ->
            val inWeek = timed.filter { (_, s) -> s.end > week * DAY && s.start < (week + 7) * DAY }
            if (inWeek.isEmpty()) return@mapNotNull null
            val rest = LabourCode.weeklyRestMinutes(spans, week)
            if (rest >= LabourCode.WEEKLY_REST_HOURS * 60L) return@mapNotNull null
            LabourWarning.ShortWeeklyRest(userId, inWeek.map { it.first.id }, LocalDate.fromEpochDays(week).toString(), rest / 60.0, basis)
        }.toList()
    }

    private fun hours(
        userId: String,
        timed: List<Pair<Shift, MinuteSpan>>,
        profile: LabourProfile,
        days: Int,
        nominalMonthlyHours: Double
    ): List<LabourWarning> = buildList {
        val byDate = timed.groupBy { it.first.date.take(10) }
        val minutesPerDay = byDate.mapValues { (_, day) -> day.sumOf { (it.second.end - it.second.start).toInt() } }
        val normMinutes = profile.dailyNormHours * 60
        byDate.entries.sortedBy { it.key }.forEach { (date, day) ->
            val minutes = minutesPerDay.getValue(date)
            if (minutes > normMinutes + EPSILON) {
                add(LabourWarning.OverDailyNorm(userId, day.map { it.first.id }, date, minutes / 60.0, profile.dailyNormHours))
            }
        }
        val total = minutesPerDay.values.sum() / 60.0
        if (total > LabourCode.periodMaxHours(days) + EPSILON) {
            add(LabourWarning.OverWeeklyAverage(userId, total / (days / 7.0)))
        }
        val periodNorm = (profile.monthlyNormHours ?: nominalMonthlyHours) * 60
        val overtime = LabourCode.overtimeMinutes(minutesPerDay.values, normMinutes, periodNorm) / 60.0
        if (overtime > LabourCode.MONTHLY_OVERTIME_HOURS + EPSILON) {
            add(LabourWarning.OvertimeOverCap(userId, overtime, LabourCode.MONTHLY_OVERTIME_HOURS))
        }
    }

    private const val DAY = 24 * 60L
    private const val EPSILON = 1e-6

    private fun Shift.span(): MinuteSpan? {
        val start = isoMinutes(plannedTime.start) ?: return null
        val end = isoMinutes(plannedTime.end) ?: return null
        return MinuteSpan(start, end).takeIf { end > start }
    }
}
